import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import AuthRoute from './AuthRoute';
import { USER_ROLE } from '@/shared/constants';

import MainLayout from '@/shared/components/layout/MainLayout';
import PageWrapper from './PageWrapper';

import { PATH } from '@/shared/constants';

const AdminHomePage = lazy(() => import('@/pages/admin/home/Page'));

const AdminStudentPage = lazy(() => import('@/pages/admin/student/Page'));
const AdminStudentDetailPage = lazy(() => import('@/pages/admin/student/detail/[studentId]/Page'));
const AdminStudentAddPage = lazy(() => import('@/pages/admin/student/add/Page'));
const AdminStudentSettingPage = lazy(() => import('@/pages/admin/student/setting/Page'));

const AdminInstructorPage = lazy(() => import('@/pages/admin/instructor/Page'));
const AdminInstructorAddPage = lazy(() => import('@/pages/admin/instructor/add/Page'));
const AdminInstructorDetailPage = lazy(() => import('@/pages/admin/instructor/detail/[instructorId]/Page'));

const AdminStaffPage = lazy(() => import('@/pages/admin/staff/Page'));
const AdminStaffAddPage = lazy(() => import('@/pages/admin/staff/add/Page'));
const AdminStaffSettingPage = lazy(() => import('@/pages/admin/staff/setting/Page'));
const AdminStaffDetailPage = lazy(() => import('@/pages/admin/staff/detail/[staffId]/Page'));

const AdminLessonPage = lazy(() => import('@/pages/admin/lesson/Page'));
const AdminLessonAddPage = lazy(() => import('@/pages/admin/lesson/add/Page'));
const AdminLessonSettingPage = lazy(() => import('@/pages/admin/lesson/setting/Page'));
const AdminLessonDetailPage = lazy(() => import('@/pages/admin/lesson/[lessonId]/Page'));

const AdminLessonBoardLayout = lazy(() => import('@/pages/admin/lesson/board/[lessonId]/Layout'));
const AdminLessonBoardClassNoticePage = lazy(() => import('@/pages/admin/lesson/board/[lessonId]/class-notice/Page'));
const AdminLessonBoardNoticeDetailPage = lazy(() => import('@/pages/admin/lesson/board/[lessonId]/class-notice/[noticeId]/Page'));
const AdminLessonBoardProgressVodPage = lazy(() => import('@/pages/admin/lesson/board/[lessonId]/progress-vod/Page'));
const AdminLessonBoardProgressVodDetailPage = lazy(() => import('@/pages/admin/lesson/board/[lessonId]/progress-vod/[vodId]/Page'));
const AdminLessonBoardQuizAssignmentPage = lazy(() => import('@/pages/admin/lesson/board/[lessonId]/quiz-assignment/Page'));
const AdminLessonBoardQuizAssignmentDetailPage = lazy(() => import('@/pages/admin/lesson/board/[lessonId]/quiz-assignment/[quizAssignmentId]/Page'));
const AdminLessonBoardWritePage = lazy(() => import('@/pages/admin/lesson/board/[lessonId]/write/Page'));
const AdminLessonBoardEditPage = lazy(() => import('@/pages/admin/lesson/board/[lessonId]/edit/Page'));

const AdminAttendancePage = lazy(() => import('@/pages/admin/attendance/Page'));
const AdminZoomAccountPage = lazy(() => import('@/pages/admin/attendance/zoom-account/Page'));

const AdminNoticePage = lazy(() => import('@/pages/admin/notice/Page'));
const AdminNoticeAddPage = lazy(() => import('@/pages/admin/notice/add/Page'));
const AdminNoticeDetailPage = lazy(() => import('@/pages/admin/notice/detail/[noticeId]/Page'));
const AdminNoticeEditPage = lazy(() => import('@/pages/admin/notice/edit/[noticeId]/Page'));

const AdminPaymentPage = lazy(() => import('@/pages/admin/payment/Page'));
const AdminCounselingPage = lazy(() => import('@/pages/admin/counseling/Page'));
const AdminCounselingAddPage = lazy(() => import('@/pages/admin/counseling/add/Page'));
const AdminCounselingDetailPage = lazy(() => import('@/pages/admin/counseling/detail/[consultationId]/Page'));
const AdminKakaoTalkPage = lazy(() => import('@/pages/admin/kakao-talk/Page'));

const AdminEnrollmentPage = lazy(() => import('@/pages/admin/enrollment/Page'));
const AdminNotificationAutomationPage = lazy(() => import('@/pages/admin/notification/automation/Page'));

export const adminRoutes: RouteObject = {
  path: PATH.ADMIN.INDEX,
  element: <AuthRoute allowedRoles={[USER_ROLE.ADMIN, USER_ROLE.STAFF]} />,
  children: [
    {
      element: <MainLayout />,
      children: [
        { index: true, element: <Navigate to={PATH.ADMIN.HOME} replace /> },
        {
          path: PATH.ADMIN.NOTIFICATION.AUTOMATION,
          element: (
            <PageWrapper>
              <AdminNotificationAutomationPage />
            </PageWrapper>
          ),
        },
        {
          path: PATH.ADMIN.HOME,
          element: (
            <PageWrapper>
              <AdminHomePage />
            </PageWrapper>
          ),
        },
        {
          path: PATH.ADMIN.STUDENT.INDEX,
          children: [
            {
              index: true,
              element: (
                <PageWrapper>
                  <AdminStudentPage />
                </PageWrapper>
              ),
            },
            {
              path: PATH.ADMIN.STUDENT.ADD,
              element: (
                <PageWrapper>
                  <AdminStudentAddPage />
                </PageWrapper>
              ),
            },
            {
              path: `${PATH.ADMIN.STUDENT.DETAIL.INDEX}:studentId`,
              element: (
                <PageWrapper>
                  <AdminStudentDetailPage />
                </PageWrapper>
              ),
            },
            {
              path: PATH.ADMIN.STUDENT.SETTING,
              element: (
                <PageWrapper>
                  <AdminStudentSettingPage />
                </PageWrapper>
              ),
            },
          ],
        },
        {
          path: PATH.ADMIN.INSTRUCTOR.INDEX,
          children: [
            {
              index: true,
              element: (
                <PageWrapper>
                  <AdminInstructorPage />
                </PageWrapper>
              ),
            },
            {
              path: PATH.ADMIN.INSTRUCTOR.ADD,
              element: (
                <PageWrapper>
                  <AdminInstructorAddPage />
                </PageWrapper>
              ),
            },
            {
              path: `${PATH.ADMIN.INSTRUCTOR.DETAIL.INDEX}:instructorId`,
              element: (
                <PageWrapper>
                  <AdminInstructorDetailPage />
                </PageWrapper>
              ),
            },
          ],
        },

        {
          path: PATH.ADMIN.STAFF.INDEX,
          children: [
            {
              index: true,
              element: (
                <PageWrapper>
                  <AdminStaffPage />
                </PageWrapper>
              ),
            },
            {
              path: PATH.ADMIN.STAFF.ADD,
              element: (
                <PageWrapper>
                  <AdminStaffAddPage />
                </PageWrapper>
              ),
            },
            {
              path: PATH.ADMIN.STAFF.SETTING,
              element: (
                <PageWrapper>
                  <AdminStaffSettingPage />
                </PageWrapper>
              ),
            },
            {
              path: `${PATH.ADMIN.STAFF.DETAIL.INDEX}:staffId`,
              element: (
                <PageWrapper>
                  <AdminStaffDetailPage />
                </PageWrapper>
              ),
            },
          ],
        },

        // 수업 (lesson)
        {
          path: PATH.ADMIN.LESSON.INDEX,
          children: [
            {
              index: true,
              element: (
                <PageWrapper>
                  <AdminLessonPage />
                </PageWrapper>
              ),
            },
            {
              path: PATH.ADMIN.LESSON.ADD,
              element: (
                <PageWrapper>
                  <AdminLessonAddPage />
                </PageWrapper>
              ),
            },
            {
              path: PATH.ADMIN.LESSON.SETTING,
              element: (
                <PageWrapper>
                  <AdminLessonSettingPage />
                </PageWrapper>
              ),
            },
            {
              path: `${PATH.ADMIN.LESSON.INDEX}/:lessonId`,
              element: (
                <PageWrapper>
                  <AdminLessonDetailPage />
                </PageWrapper>
              ),
            },
            {
              path: PATH.ADMIN.LESSON.BOARD.INDEX,
              children: [
                {
                  path: ':lessonId',
                  element: (
                    <PageWrapper>
                      <AdminLessonBoardLayout />
                    </PageWrapper>
                  ),
                  children: [
                    { index: true, element: <Navigate to='class-notice' replace /> },
                    {
                      path: 'class-notice',
                      element: (
                        <PageWrapper>
                          <AdminLessonBoardClassNoticePage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'class-notice/:noticeId',
                      element: (
                        <PageWrapper>
                          <AdminLessonBoardNoticeDetailPage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'progress-vod',
                      element: (
                        <PageWrapper>
                          <AdminLessonBoardProgressVodPage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'progress-vod/:vodId',
                      element: (
                        <PageWrapper>
                          <AdminLessonBoardProgressVodDetailPage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'quiz-assignment',
                      element: (
                        <PageWrapper>
                          <AdminLessonBoardQuizAssignmentPage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'quiz-assignment/:quizAssignmentId',
                      element: (
                        <PageWrapper>
                          <AdminLessonBoardQuizAssignmentDetailPage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'write',
                      element: (
                        <PageWrapper>
                          <AdminLessonBoardWritePage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'edit/:postId',
                      element: (
                        <PageWrapper>
                          <AdminLessonBoardEditPage />
                        </PageWrapper>
                      ),
                    },
                  ],
                },
              ],
            },
          ],
        },

        {
          path: PATH.ADMIN.ATTENDANCE.INDEX,
          element: (
            <PageWrapper>
              <AdminAttendancePage />
            </PageWrapper>
          ),
        },
        {
          path: PATH.ADMIN.ATTENDANCE.ZOOM_ACCOUNT,
          element: (
            <PageWrapper>
              <AdminZoomAccountPage />
            </PageWrapper>
          ),
        },
        {
          path: PATH.ADMIN.NOTICE.INDEX,
          children: [
            {
              index: true,
              element: (
                <PageWrapper>
                  <AdminNoticePage />
                </PageWrapper>
              ),
            },
            {
              path: PATH.ADMIN.NOTICE.ADD,
              element: (
                <PageWrapper>
                  <AdminNoticeAddPage />
                </PageWrapper>
              ),
            },
            {
              path: `${PATH.ADMIN.NOTICE.DETAIL}:noticeId`,
              element: (
                <PageWrapper>
                  <AdminNoticeDetailPage />
                </PageWrapper>
              ),
            },
            {
              path: `${PATH.ADMIN.NOTICE.EDIT}:noticeId`,
              element: (
                <PageWrapper>
                  <AdminNoticeEditPage />
                </PageWrapper>
              ),
            },
          ],
        },
        {
          path: PATH.ADMIN.PAYMENT.INDEX,
          element: (
            <PageWrapper>
              <AdminPaymentPage />
            </PageWrapper>
          ),
        },
        {
          path: PATH.ADMIN.COUNSELING.INDEX,
          children: [
            {
              index: true,
              element: (
                <PageWrapper>
                  <AdminCounselingPage />
                </PageWrapper>
              ),
            },
            {
              path: PATH.ADMIN.COUNSELING.ADD,
              element: (
                <PageWrapper>
                  <AdminCounselingAddPage />
                </PageWrapper>
              ),
            },
            {
              path: `${PATH.ADMIN.COUNSELING.DETAIL.INDEX}:consultationId`,
              element: (
                <PageWrapper>
                  <AdminCounselingDetailPage />
                </PageWrapper>
              ),
            },
          ],
        },
        {
          path: PATH.ADMIN.KAKAO_TALK.INDEX,
          element: (
            <PageWrapper>
              <AdminKakaoTalkPage />
            </PageWrapper>
          ),
        },

        {
          path: PATH.ADMIN.ENROLLMENT.INDEX,
          element: (
            <PageWrapper>
              <AdminEnrollmentPage />
            </PageWrapper>
          ),
        },
      ],
    },
  ],
};
